/**
 * FLAGS de la migración de sync a REST (plan `docs/plans/06-migracion-rest.md`).
 *
 * Cada etapa de la migración lleva un flag que gatea el **camino de lectura del
 * cliente** y sirve de palanca de rollback inmediata mientras el stream de
 * PowerSync equivalente sigue vivo. Un flag NO recorta un stream (decisión
 * explícita del plan, §7): el recorte viaja en un commit posterior y su
 * rollback es `git revert` + `rollout restart`.
 *
 * IMPORTANTE — lo que estos flags NO gatean: el control de acceso. La redacción
 * del correo por capacidad (`org_rbac::can_view_member_emails`) es
 * INCONDICIONAL en el servidor. Colgarla de un flag habría convertido el
 * interruptor de rollback en un interruptor para reabrir la fuga, que es
 * justamente el defecto que la etapa cierra. Por eso estas features NO son
 * `gateable`: no tienen —ni deben tener— `feature_gate` en ningún write-path.
 */

import type { FeatureManifest } from "./country/packs";

/**
 * E2 · identidades de miembros por REST en vez de por el stream `organizations`.
 *
 * El defecto que cierra (D3) no era el transporte sino el filtro: el stream
 * repartía el correo de todo el personal y de todos los compañeros a cualquier
 * miembro. Con el flag encendido, la identidad del directorio y de la ficha de
 * miembro se sirve por `GET /api/org/listMembers`, que sí gatea el correo por
 * capacidad; con él apagado, el cliente vuelve a leer la identidad ya replicada
 * (la palanca de rollback, que muere con el recorte del stream en E3).
 *
 * **E3 lo reutiliza** en vez de estrenar flag: las cuatro pantallas que
 * quedaban con join de identidad (roster de cohorte, grupos, informe de
 * asistencia y alertas) y el directorio con expediente conmutan la MISMA
 * decisión —«¿la identidad de terceros viene del servidor o del espejo
 * local?»— sobre las mismas filas de `"user"`. Dos interruptores para una
 * decisión sola solo sirven para dejar media migración encendida.
 */
const REST_IDENTITIES: FeatureManifest = {
  key: "academy.data.rest_identities",
  name: "Identidades de miembros por API",
  description:
    "Sirve el nombre, la imagen y —solo a quien puede gestionar miembros o leer datos de sede— el correo de los miembros de la academia por API en vez de replicarlos a cada dispositivo. Un alumno deja de recibir los correos de sus compañeros y del personal.",
  scope: "org",
  status: "beta",
  since: "2026-07",
  docsSlug: "administrador",
  // Sin aristas duras: la identidad de miembros no depende de ninguna otra
  // feature, y ninguna otra debe quedar rehén de esta palanca de rollback.
  requires: [],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Hidratar nombre/correo del directorio de gente desde la API (se vuelve al dato replicado)",
      "Hidratar la identidad de la ficha de miembro desde la API",
      "Hidratar la identidad del roster, el informe de asistencia y las alertas de una cohorte desde la API",
      "Servir el directorio con expediente desde la API (se vuelve a los perfiles replicados)",
    ],
    dataPolicy: "preserved",
  },
};

/** Manifests de la migración de acceso a datos (fuente `data_access`). */
export function dataAccessManifests(): FeatureManifest[] {
  return [REST_IDENTITIES];
}

/** Key del flag de E2 (greppable desde el cliente). */
export const FLAG_REST_IDENTITIES = REST_IDENTITIES.key;
